import { Box, CircularProgress } from 'src/components';
import { useIsMobile, useChatMessages } from 'src/hooks';
import { useParams, useOutletContext } from "react-router-dom";
import { useAuth, getChatByOtherUserId, getChatById, sendMessage, getMessages } from "src/utils";
import { useEffect, useState } from "react";
import { mapUserChat } from 'src/data';
import { PushNotifications } from '@capacitor/push-notifications';

import ChatWindowHeader from './chat-window-header';
import MessageList from './message-list';
import MessageInput from './message-input';

export default function ChatWindow({ type, userHasUnreadChats }) {
    const isMobile = useIsMobile();
    const { userId, groupId } = useParams();
    const { setAllChats, toggleSidebar, footerHeight } = useOutletContext();
    const { user } = useAuth();

    const isAIChat = type === "ai";
    const MESSAGE_INCREMENT = 20;

    const [chat, setChat] = useState(null);     
    const [messages, setMessages] = useState([]); 
    const [skip, setSkip] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sendingMessage, setSendingMessage] = useState(false);

    useEffect(() => {
        if (isAIChat) {
            setChat({chatName: "PanAm Assistant"});
        }
    }, [isAIChat]);

    useEffect(() => {
        if (chat?.messages) {
            setMessages(chat.messages);
            setSkip(chat.messages.length);
        }
    }, [chat]);

    useEffect(() => {
        if (chat?.id) {
            PushNotifications.removeAllDeliveredNotifications().catch((err) => {
                console.warn('removeAllDeliveredNotifications failed (known bug):', err);
            });
        }
    }, [chat?.id]);  

    useEffect(() => {
        async function loadChat() {
            setLoading(true);

            try {
                let res;
                if (type === "direct") {
                    res = await getChatByOtherUserId(userId);
                } else if (type === "group") {
                    res = await getChatById(groupId);
                }

                setChat(res);
                setMessages(res?.messages || []); 
                setAllChats((prev) => {
                    const newChat = mapUserChat(res, user.id, type);
                    const exists = prev.some((c) => c.id === newChat.id);
                    if (exists) {
                        return prev.map((c) => (c.id === newChat.id ? newChat : c));
                    } else {
                        return [...prev, newChat];
                    }
                });
            } catch (err) {
                console.error("Error fetching chat", err);
            }

            setLoading(false);
        }

        if (type === "direct" || type === "group") {
            loadChat();
        }
    }, [type, userId, groupId, user]);

    const handleSendMessage = async (text, retryId = null) => {
        setSendingMessage(true);
        const tempId = retryId || `temp-${Date.now()}`;

        // Optimistic update
        if (!retryId) {
            const newMessage = {
                tempId,   
                id: null, 
                text,
                senderId: user.id,
                senderName: user.name,
                senderImageUrl: user.profileImageUrl,
                content: { text },
                timestamp: new Date().toISOString(),
                status: "sending",
            };
            setMessages(prev => [...prev, newMessage]);
        } else {
            // retrying failed message
            setMessages(prev => prev.map(msg => msg.tempId === retryId ? { ...msg, status: "sending" } : msg));
        }

        try {
            const savedMessage = await sendMessage(chat.id, user.id, text, tempId);
            savedMessage.senderName = user.name;
            savedMessage.senderImageUrl = user.profileImageUrl;
            savedMessage.status = "sent";
            savedMessage.tempId = tempId; 

            setMessages(prev =>
                prev.map(msg => (msg.tempId === tempId ? savedMessage : msg))
            );
        } catch (err) {
            console.error("Failed to send message", err);
            setMessages(prev =>
                prev.map(msg => (msg.tempId === tempId ? { ...msg, status: "failed" } : msg))
            );
        } finally {
            setSendingMessage(false);
        }
    };

    const handleIncomingMessage = (message) => {
        setMessages(prev => {
            // Ignore exact duplicates (same server id)
            if (message.id && prev.some(m => m.id === message.id)) return prev;

            // Replace optimistic message if tempId matches
            const tempIndex = prev.findIndex(m => m.tempId && m.tempId === message.tempId);
            if (tempIndex !== -1) {
                const newMessages = [...prev];
                newMessages[tempIndex] = { ...message, status: "sent", tempId: message.tempId };
                return newMessages;
            }

            // Extra guard: if same text + sender + timestamp within 2s exists
            if (prev.some(m =>
                m.senderId === message.senderId &&
                m.content?.text === message.content?.text &&
                Math.abs(new Date(m.timestamp) - new Date(message.timestamp)) < 2000
                )) {
                    return prev;
            }

            // Append new message from other users
            return [...prev, { ...message, status: "sent" }];
        });
    };

    useChatMessages(chat?.id, handleIncomingMessage);

    const handleLoadOlderMessages = async () => {
        try {
            const res = await getMessages(chat.id, MESSAGE_INCREMENT, skip);
            if (res.length > 0) {
                setMessages((prev) => [...res, ...prev]); 
                setSkip((prev) => prev + res.length);
            }
            return res; 
        } catch (err) {
            console.error("Error loading older messages", err);
            return [];
        }
    };

    if (loading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    height: "100%", 
                }}>
                <CircularProgress size={32} />
            </Box>
        );
    }

    return (
        <Box
            flex={1}
            display="flex"
            flexDirection="column"
            bgcolor="#fff"
            height="100%"
            overflow="hidden"
            sx={{
                pt: isMobile ? 2 : 0}}>

            <ChatWindowHeader 
                chat={chat} 
                isAIChat={isAIChat}
                userId={userId}
                toggleSidebar={toggleSidebar}
                userHasUnreadChats={userHasUnreadChats} />

            <Box sx={{ flexGrow: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
                <MessageList 
                    chatId={chat?.id}
                    messages={messages} 
                    setMessages={setMessages}
                    onLoadOlderMessages={handleLoadOlderMessages}
                    onRetry={(failedMessage) => handleSendMessage(failedMessage.text, failedMessage.id)} 
                    isAIChat={isAIChat}
                />
            </Box>

            <MessageInput 
                disableInput={isAIChat}
                footerHeight={footerHeight}
                isSendingMessage={sendingMessage}
                onSend={handleSendMessage} 
            />

        </Box>
    );
};
